/**
 * UserTranscriptRow (#1009) — dedicated live-transcription row for the
 * tablet-portrait in-call view.
 *
 * Before #1009 the elderly user's STT transcript was rendered cramped
 * inside the CallStrip (small, italic, dimmed — a palette tuned for the
 * dark strip background). Bas asked for it to move to its own readable
 * row BENEATH Berta's reply so the user (and a caregiver looking over
 * their shoulder) can comfortably read what is being transcribed.
 *
 * Design (decided with Bas on the #1009 story):
 *   • Readable & clean — NOT italic, full opacity, dark ink on the
 *     light focal-area background. (CallStrip's TranscriptText uses
 *     ivory-on-dark, which is the wrong palette here.)
 *   • Smaller than Berta's reply (BertaReplyPanel floors at 28 px); this
 *     row floors at 18 px so it reads as secondary to Berta's words
 *     while staying above the Samsung A9 elderly-readability floor.
 *   • Capped at ~25 % of the viewport height ("max height 25 % WHEN
 *     having text") and bottom-anchored, so as a live partial transcript
 *     grows past the cap the OLDEST words scroll off the top and the
 *     most-recent words stay in view (same rationale as the CallStrip
 *     TranscriptText flex-end anchor — see CallStrip.transcriptAnchor
 *     test / #852).
 *   • Renders NOTHING when there is no transcript, so Berta's reply
 *     reclaims the full focal area between turns.
 *
 * Portrait-only scoping lives in the caller (TabletApp); this component
 * is layout-agnostic and simply renders the row when given text.
 *
 * Pure presentation — no state. The `transcript` prop is the same live
 * value CallStrip used to receive.
 */
import React from 'react';

// App ink — matches BertaReplyPanel's body colour so the two stacked
// text areas read as one family on the warm focal background.
const INK = '#2C2C2C';

export default function UserTranscriptRow({ transcript }) {
  // "max height 25% WHEN having text" — no text, no row.
  if (!transcript) return null;
  return (
    <div
      data-testid="user-transcript-row"
      style={{
        flex: '0 1 auto',
        maxHeight: '25dvh',
        overflowY: 'auto',
        overflowX: 'hidden',
        // #1032 — keep an over-scroll from chaining to the page (pull-to-refresh).
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        // Bottom-anchor the live text (see file header).
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        width: '100%',
        boxSizing: 'border-box',
        padding: '10px clamp(12px, 3vw, 28px)',
        borderTop: '1px solid rgba(0,0,0,0.08)',
      }}
    >
      <div
        style={{
          color: INK,
          // Smaller than Berta's reply (28 px floor) but comfortably
          // above the A9 readability floor.
          fontSize: 'clamp(18px, 2.6vw, 26px)',
          lineHeight: 1.35,
          wordBreak: 'break-word',
        }}
      >
        {transcript}
      </div>
    </div>
  );
}
